import { Badge, Button, Icons } from '@commenda/kakashi';
import type { ComponentTemplate } from '@commenda/types';
import { cn } from '@commenda/utils';
import { isModuleLockingComponent } from '../../types';
import { useDraggable } from '@dnd-kit/core';
import {
  AlertCircle,
  AlignLeft,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  CreditCard,
  ExternalLink,
  FileText,
  Hash,
  List,
  Lock,
  Mail,
  MapPin,
  MousePointer,
  Phone,
  Plus,
  Search,
  Type,
  Upload,
} from 'lucide-react';
import { FormBuilderHook } from '../../hooks/useFormBuilder';
import { COMMENDA_OS_COMPONENT_TYPES } from './components';

interface DraggableComponentProps {
  template: ComponentTemplate;
  formBuilder?: FormBuilderHook;
  onComponentAdded?: (id: string) => void;
}

// Icon mapping for component templates
const iconMap: Record<string, React.FC<{ className?: string }>> = {
  Type,
  Mail,
  AlignLeft,
  Hash,
  Lock,
  Phone,
  ChevronDown,
  Circle: Icons.CheckCircleBroken,
  CheckSquare,
  Search,
  Upload,
  MapPin,
  AlertCircle,
  MousePointer,
  List,
  CheckCircle,
  ExternalLink,
  CreditCard,
  FileText,
};

export default function DraggableComponent({
  template,
  formBuilder,
  onComponentAdded,
}: DraggableComponentProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${template.type}-${template.name}`,
    data: {
      type: 'new-component',
      template,
    },
  });

  const IconComponent = iconMap[template.icon] || Type;

  const isModuleSelected =
    formBuilder?.selection?.type === 'module' &&
    formBuilder.selection.stepId &&
    formBuilder.selection.moduleId;

  // Determine if the currently selected module is locked to a NestedForm
  const selectedModule =
    isModuleSelected && formBuilder
      ? formBuilder.dsl.steps
          .find((s) => s.id === formBuilder.selection.stepId)
          ?.modules.find((m) => m.id === formBuilder.selection.moduleId)
      : null;
  const isSelectedModuleLocked =
    selectedModule != null &&
    selectedModule.components.length === 1 &&
    isModuleLockingComponent(selectedModule.components[0].component) &&
    !isModuleLockingComponent(template.type);

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!formBuilder || !isModuleSelected) return;
    const { stepId, moduleId } = formBuilder.selection;
    if (!stepId || !moduleId) return;

    // NestedForm, KeyPersons and Ownership always create their own new module
    if (isModuleLockingComponent(template.type)) {
      const result = formBuilder.addModuleWithSingleComponent(stepId, {
        ...template.defaultConfig,
        fieldId: undefined,
      });
      if (result) {
        onComponentAdded?.(result.componentId);
      }
      return;
    }

    const newComponentId = formBuilder.addComponent(stepId, moduleId, {
      ...template.defaultConfig,
      fieldId: undefined,
    });
    if (newComponentId) {
      onComponentAdded?.(newComponentId);
    }
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border border-border-secondary',
        'bg-bg-primary hover:bg-bg-secondary cursor-grab active:cursor-grabbing',
        'transition-all duration-150',
        isDragging && 'opacity-50 shadow-lg ring-2 ring-border-brand'
      )}
    >
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-tertiary shrink-0">
        <IconComponent className="w-4 h-4 text-text-secondary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="txt-small-medium text-text-primary truncate">
            {template.name}
          </p>
          {COMMENDA_OS_COMPONENT_TYPES.has(template.type) && (
            <Badge variant="gray" size="sm">
              Commenda OS
            </Badge>
          )}
        </div>
        <p className="txt-xs-regular text-text-tertiary truncate">
          {template.description}
        </p>
      </div>
      {isModuleSelected && !isSelectedModuleLocked && (
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          className="shrink-0"
          onClick={handleAddClick}
          title={`Add ${template.name} to section`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
